// modified from https://github.com/stackblitz/vscode/blob/master/src/vs/editor-core/frameworks.ts

import * as nodePath from 'node:path';
import type { AppFiles } from '~/lib/projects';
import type { TemplateID } from '~/templates';
import { WORK_DIR } from '~/utils/constants';

export const NETLIFY_TOML = 'netlify.toml';
export const PACKAGE_JSON = 'package.json';
export const PACKAGE_LOCK = 'package-lock.json';
export const BOLT_CONFIG_JSON = '.bolt/config.json';

type PackageManager = 'npm' | 'pnpm' | 'yarn';

export interface PackageJson {
  scripts?: Record<string, string>;
  dependencies?: Record<string, string>;
  devDependencies?: Record<string, string>;
}

export interface BoltConfigJson {
  template?: TemplateID;
  build?: {
    command: string;
    output: string;
  };
}

export type FrameworkName =
  | 'nextjs'
  | 'remix'
  | 'nuxt3'
  | 'nuxt2'
  | 'sveltekit'
  | 'nestjs'
  | 'astro'
  | 'docusaurus'
  | 'slidev'
  | 'eleventy'
  | 'nativescript'
  | 'expo'
  | 'vite-react'
  | 'DEFAULT_TO_DEV'
  | 'DEFAULT_TO_START';

export interface Framework {
  name: FrameworkName;
  startCommand: (packageManager: PackageManager) => string;
  build?: {
    command: (packageManager: PackageManager) => string;
    output: string;
  };
  detectors: {
    every?: Array<(packageJson: PackageJson) => boolean>;
    some?: Array<(packageJson: PackageJson) => boolean>;
  };
  redirects?: string;
}

export const frameworks: Framework[] = [
  {
    name: 'nextjs',
    startCommand: () => 'npx next dev',
    detectors: {
      every: [matchDependency('next')],
    },
  },
  {
    name: 'remix',
    startCommand: (packageManager) => `${packageManager} run dev`,
    detectors: {
      every: [matchDependency('remix'), matchScript('dev')],
    },
  },
  {
    name: 'nuxt3',
    startCommand: (packageManager) => `${packageManager} run dev`,
    detectors: {
      every: [matchDependency('nuxt3'), matchScript('dev')],
    },
  },
  {
    name: 'nuxt2',
    startCommand: (packageManager) => `${packageManager} run dev`,
    detectors: {
      every: [matchDependency('nuxt'), matchScript('dev')],
    },
  },
  {
    name: 'sveltekit',
    startCommand: (packageManager) => `${packageManager} run dev`,
    detectors: {
      every: [matchDependency('@sveltejs/kit'), matchScript('dev')],
    },
  },
  {
    name: 'nestjs',
    startCommand: (packageManager) => `${packageManager} start`,
    detectors: {
      every: [matchDependency('@nestjs/core'), matchScript('start')],
    },
  },
  {
    name: 'astro',
    startCommand: (packageManager) => `${packageManager} run dev`,
    build: {
      command: () => 'npx astro build',
      output: 'dist',
    },
    detectors: {
      every: [matchDependency('astro'), matchScript('dev')],
    },
  },
  {
    name: 'docusaurus',
    startCommand: (packageManager) => `${packageManager} start`,
    detectors: {
      every: [matchDependency('@docusaurus/core'), matchScript('start')],
    },
  },
  {
    name: 'slidev',
    startCommand: (packageManager) => `${packageManager} run dev`,
    build: {
      command: () => 'npx slidev build',
      output: 'dist',
    },
    detectors: {
      every: [matchDependency('@slidev/cli'), matchScript('dev')],
    },
  },
  {
    name: 'eleventy',
    startCommand: (packageManager) => `${packageManager} start`,
    detectors: {
      every: [matchDependency('@11ty/eleventy'), matchScript('start')],
    },
  },
  {
    name: 'nativescript',
    startCommand: () => 'setup-nativescript-stackblitz && ns preview',
    detectors: {
      every: [matchDependency('@nativescript/core'), matchDependency('@nativescript/stackblitz')],
    },
  },
  {
    name: 'expo',
    startCommand: (packageManager) => `${packageManager} run dev`,
    detectors: {
      every: [matchDependency('expo'), matchDependency('react-native')],
    },
  },
  {
    name: 'vite-react',
    startCommand: (packageManager) => `${packageManager} run dev`,
    detectors: {
      every: [matchDependency('vite'), matchDependency('@vitejs/plugin-react')],
    },
    redirects: '/*    /index.html   200',
  },
  {
    name: 'DEFAULT_TO_DEV',
    startCommand: (packageManager) => `${packageManager} run dev`,
    detectors: {
      every: [matchScript('dev')],
    },
  },
  {
    name: 'DEFAULT_TO_START',
    startCommand: (packageManager) => `${packageManager} start`,
    detectors: {
      every: [matchScript('start')],
    },
  },
];

export function isBuildCommand(command: string) {
  return frameworks.some((framework) => framework.build?.command('npm') === command);
}

function matchDependency(name: string) {
  return (packageJson: PackageJson) => {
    return Boolean(packageJson.dependencies?.[name] || packageJson.devDependencies?.[name]);
  };
}

function matchScript(name: string) {
  return (packageJson: PackageJson) => {
    return Boolean(packageJson.scripts?.[name]);
  };
}

export function readPackageJson(files: AppFiles): PackageJson | undefined {
  const paths = Object.keys(files);
  const relative = nodePath.join(WORK_DIR, PACKAGE_JSON);

  if (paths.includes(relative) && files[relative]?.type === 'file') {
    try {
      return JSON.parse(files[relative].contents);
    } catch {}
  }

  return undefined;
}

export function getFramework(packageJson: PackageJson): Framework | undefined {
  return frameworks.find(({ detectors }) => {
    const results: boolean[] = [];

    if (detectors.every?.length) {
      results.push(detectors.every.every((test) => test(packageJson)));
    }

    if (detectors.some?.length) {
      results.push(detectors.some.some((test) => test(packageJson)));
    }

    return results.length > 0 && results.every(Boolean);
  });
}
